import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Sparkles, Shield, Zap, CheckCircle, Loader, ArrowRight, Lock } from 'lucide-react';
import { createCheckoutSession, hasVerifiedPayment, verifySession } from '../utils/stripeApi';
import EmbeddedCheckout from './EmbeddedCheckout';

const GateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 2rem;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const SuccessCard = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
  animation: fadeInUp 0.6s ease-out;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SuccessTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const SuccessMessage = styled.p`
  font-size: 1.1rem;
  opacity: 0.95;
  margin: 0;
`;

const CTAHero = styled.div`
  margin-bottom: 3rem;
  animation: fadeIn 0.8s ease-out;
`;

const CTATitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: 1.375rem;
  color: #64748b;
  margin-bottom: 2.5rem;
  line-height: 1.7;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
  width: 100%;
`;

const BenefitCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -4px rgba(0, 0, 0, 0.15);
  }
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(58, 92, 247, 0.1) 0%, rgba(58, 92, 247, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  color: #3a5cf7;
`;

const BenefitTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.75rem;
`;

const BenefitDescription = styled.p`
  color: #64748b;
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #3a5cf7 0%, #2d4ae8 100%);
  color: white;
  border: none;
  padding: 1rem 4rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0 auto;
  box-shadow: 0 8px 16px -4px rgba(58, 92, 247, 0.4);
  width: 100%;
  max-width: 600px;
  min-height: 56px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -4px rgba(58, 92, 247, 0.5);
    background: linear-gradient(135deg, #2d4ae8 0%, #1e3a8a 100%);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const TrustBadges = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 0.875rem;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-top: 1rem;
  font-size: 0.9375rem;
`;

function PaymentGate({ onPaymentVerified, children, onCheckoutTrigger }) {
  const [isPaid, setIsPaid] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPayment = async () => {
      // Check if payment was verified in this session
      if (hasVerifiedPayment()) {
        setIsPaid(true);
        setIsChecking(false);
        if (onPaymentVerified) onPaymentVerified();
        return;
      }

      // Check URL for session_id (from Stripe redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId) {
        try {
          const verification = await verifySession(sessionId);
          if (verification.paid) {
            setIsPaid(true);
            sessionStorage.setItem('payment_verified', 'true');
            sessionStorage.setItem('stripe_session_id', sessionId);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            if (onPaymentVerified) onPaymentVerified();
          } else {
            setError('Payment verification failed. Please try again.');
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          setError('Failed to verify payment. Please contact support.');
        }
      }
      
      setIsChecking(false);
    };

    checkPayment();
  }, [onPaymentVerified]);

  const handleCheckoutClick = () => {
    setShowCheckout(true);
    setError(null);
  };

  // Expose checkout trigger function to parent
  useEffect(() => {
    if (onCheckoutTrigger) {
      onCheckoutTrigger.current = handleCheckoutClick;
    }
  }, [onCheckoutTrigger]);

  const handleCheckoutSuccess = async (clientSecret) => {
    // Extract session_id from clientSecret or verify payment
    try {
      // For embedded checkout, we need to verify the session after completion
      // The return_url will have the session_id
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId) {
        const verification = await verifySession(sessionId);
        if (verification.paid) {
          setIsPaid(true);
          sessionStorage.setItem('payment_verified', 'true');
          sessionStorage.setItem('stripe_session_id', sessionId);
          if (onPaymentVerified) onPaymentVerified();
        }
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError('Payment verification failed. Please contact support.');
    }
  };

  if (isChecking) {
    return (
      <GateContainer>
        <Loader size={48} className="animate-spin" style={{ color: '#3a5cf7' }} />
        <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading...</p>
      </GateContainer>
    );
  }

  if (isPaid) {
    return (
      <>
        <GateContainer>
          <SuccessCard>
            <SuccessTitle>
              <CheckCircle size={32} />
              Payment Confirmed!
            </SuccessTitle>
            <SuccessMessage>
              You're all set! Upload your photo to see your AI rhinoplasty simulation.
            </SuccessMessage>
          </SuccessCard>
        </GateContainer>
        {children}
      </>
    );
  }

  return (
    <GateContainer>
      <CTAHero>
        <CTATitle>See Your Perfect Nose Before Surgery</CTATitle>
        <CTASubtitle>
          Get an AI-powered preview of how rhinoplasty could transform your appearance. 
          See realistic before-and-after results instantly.
        </CTASubtitle>
      </CTAHero>

      <BenefitsGrid>
        <BenefitCard>
          <BenefitIcon>
            <Sparkles size={28} />
          </BenefitIcon>
          <BenefitTitle>AI-Powered Analysis</BenefitTitle>
          <BenefitDescription>
            Advanced facial recognition and 3D mapping technology for precise simulations
          </BenefitDescription>
        </BenefitCard>

        <BenefitCard>
          <BenefitIcon>
            <Shield size={28} />
          </BenefitIcon>
          <BenefitTitle>Medical-Grade Precision</BenefitTitle>
          <BenefitDescription>
            Based on established beauty standards and facial harmony principles used by surgeons
          </BenefitDescription>
        </BenefitCard>

        <BenefitCard>
          <BenefitIcon>
            <Zap size={28} />
          </BenefitIcon>
          <BenefitTitle>Instant Results</BenefitTitle>
          <BenefitDescription>
            Get your personalized before-and-after preview in seconds, not weeks
          </BenefitDescription>
        </BenefitCard>
      </BenefitsGrid>

      {!showCheckout ? (
        <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto 2rem' }}>
          <CTAButton onClick={handleCheckoutClick}>
            <Sparkles size={20} />
            See Your Perfect Nose in Seconds
            <ArrowRight size={20} />
          </CTAButton>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </div>
      ) : (
        <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto 2rem' }}>
          <EmbeddedCheckout onSuccess={handleCheckoutSuccess} />
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </div>
      )}

      <TrustBadges>
        <TrustBadge>
          <Zap size={16} />
          <span>Instant Results</span>
        </TrustBadge>
        <TrustBadge>
          <Shield size={16} />
          <span>Medical-Grade AI</span>
        </TrustBadge>
        <TrustBadge>
          <Lock size={16} />
          <span>Private & Secure</span>
        </TrustBadge>
      </TrustBadges>
    </GateContainer>
  );
}

export default PaymentGate;

